import type {
	FocusEventHandler,
	PointerEventHandler,
	RefAttributes,
	RefObject,
} from "react";
import type {
	ComboBoxProps as AriaComboBoxProps,
	GroupProps as AriaGroupProps,
	ListBoxProps as AriaListBoxProps,
} from "react-aria-components";

import { SearchLg as SearchIcon } from "@untitledui/icons";
import { useCallback, useContext, useRef, useState } from "react";
import {
	ComboBox as AriaComboBox,
	Group as AriaGroup,
	Input as AriaInput,
	ListBox as AriaListBox,
	ComboBoxStateContext,
} from "react-aria-components";

import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { Popover } from "@/components/ui/select/popover";
import {
	type CommonProps,
	type SelectItemType,
} from "@/components/ui/select/select";
import { SelectContext } from "@/components/ui/select/select-context";
import { sizes } from "@/components/ui/select/select-sizes";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { cx } from "@/utils/cx";

interface ComboBoxProps
	extends
		CommonProps,
		Omit<AriaComboBoxProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement> {
	children: AriaListBoxProps<SelectItemType>["children"];
	items?: SelectItemType[];
	popoverClassName?: string;
	shortcut?: boolean;
	shortcutClassName?: string;
}

interface ComboBoxValueProps extends AriaGroupProps {
	onFocus?: FocusEventHandler;
	onPointerEnter?: PointerEventHandler;
	placeholder?: string;
	ref?: RefObject<HTMLDivElement | null>;
	shortcut: boolean;
	shortcutClassName?: string;
	size: "md" | "sm";
}

const ComboBoxValue = ({
	placeholder,
	shortcut,
	shortcutClassName,
	size,
	...otherProps
}: ComboBoxValueProps) => {
	const state = useContext(ComboBoxStateContext);

	const value = state?.selectedItem?.value || null;
	const inputValue = state?.inputValue || null;

	const first = inputValue?.split(value?.supportingText)?.[0] || "";
	const last = inputValue?.split(first)[1];

	return (
		<AriaGroup
			{...otherProps}
			className={({ isDisabled, isFocusWithin }) =>
				cx(
					"relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition-shadow duration-100 ease-linear ring-inset",
					isDisabled && "cursor-not-allowed opacity-50",
					isFocusWithin && "ring-2 ring-focus-ring",
					sizes[size].root,
				)
			}
		>
			{({ isDisabled }) => (
				<>
					<SearchIcon className="pointer-events-none size-5 text-fg-quaternary" />

					<div className="relative flex w-full items-center gap-2">
						{inputValue && (
							<span
								aria-hidden="true"
								className="absolute top-1/2 z-0 inline-flex w-full -translate-y-1/2 gap-2 truncate"
							>
								<p className={cx("text-md font-medium text-primary")}>
									{first}
								</p>
								{last && (
									<p className={cx("-ml-0.75 text-md text-tertiary")}>{last}</p>
								)}
							</span>
						)}

						<AriaInput
							className="z-10 w-full appearance-none bg-transparent text-md text-transparent caret-alpha-black/90 outline-hidden placeholder:text-placeholder focus-visible:outline-hidden disabled:cursor-not-allowed"
							placeholder={placeholder}
						/>
					</div>

					{shortcut && (
						<div
							className={cx(
								"absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8",
								sizes[size].shortcut,
								shortcutClassName,
							)}
						>
							<span
								aria-hidden="true"
								className={cx(
									"pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset",
									isDisabled && "bg-transparent",
								)}
							>
								⌘K
							</span>
						</div>
					)}
				</>
			)}
		</AriaGroup>
	);
};

export const ComboBox = ({
	children,
	items,
	placeholder = "Search",
	shortcut = true,
	shortcutClassName,
	size = "sm",
	...otherProps
}: ComboBoxProps) => {
	const placeholderRef = useRef<HTMLDivElement>(null);
	const [popoverWidth, setPopoverWidth] = useState("");

	// Resize observer for popover width
	const onResize = useCallback(() => {
		if (!placeholderRef.current) return;

		const divRect = placeholderRef.current?.getBoundingClientRect();

		setPopoverWidth(divRect.width + "px");
	}, [placeholderRef, setPopoverWidth]);

	useResizeObserver({
		box: "border-box",
		onResize,
		ref: placeholderRef,
	});

	return (
		<SelectContext.Provider value={{ size }}>
			<AriaComboBox menuTrigger="focus" {...otherProps}>
				{(state) => (
					<div className="flex flex-col gap-1.5">
						{otherProps.label && (
							<Label isRequired={state.isRequired} tooltip={otherProps.tooltip}>
								{otherProps.label}
							</Label>
						)}

						<ComboBoxValue
							// This is a workaround to correctly calculating the trigger width
							// while using ResizeObserver wasn't 100% reliable.
							onFocus={onResize}
							onPointerEnter={onResize}
							placeholder={placeholder}
							ref={placeholderRef}
							shortcut={shortcut}
							shortcutClassName={shortcutClassName}
							size={size}
						/>

						<Popover
							className={otherProps.popoverClassName}
							size={size}
							style={{ width: popoverWidth }}
							triggerRef={placeholderRef}
						>
							<AriaListBox className="size-full outline-hidden" items={items}>
								{children}
							</AriaListBox>
						</Popover>

						{otherProps.hint && (
							<HintText isInvalid={state.isInvalid}>{otherProps.hint}</HintText>
						)}
					</div>
				)}
			</AriaComboBox>
		</SelectContext.Provider>
	);
};
